// Photo upload pipeline service (spec #48 – #52).
//
// Pure logic, no React. The controller (in `usePhotoUploadController.ts`)
// drives state transitions by calling into this service, which performs
// the four-step Backend contract:
//
//   1. `requestUploadUrl`     — authenticated POST
//   2. `uploadToR2(original)` — raw PUT, NO Bearer header
//   3. `uploadToR2(thumbnail)`— raw PUT, only when the Backend returned
//                                `thumbnail_upload_url`
//   4. `finalizeUpload`       — authenticated POST
//
// Idempotency:
//   The Backend dedupes on `(report_id, client_photo_id)`. We
//   therefore pass the same `task.clientPhotoId` on every retry.
//   The service is a pure function over a `task` + `ctx` — it never
//   mutates state directly; instead it returns the next `task` shape
//   via a status callback (`onUpdate`). The controller owns the
//   authoritative `tasks` array.
//
// Errors:
//   - MIME / size validation failures happen BEFORE this service is
//     called; the controller uses `validateFile()` to short-circuit
//     bad Files before they ever reach `runUploadTask`.
//   - All other failures (network, R2 4xx/5xx, Backend 4xx/5xx,
//     Backend 402 quota) propagate to the controller as thrown
//     `PhotoUploadPipelineError`. The controller marks the task
//     `failed` and exposes `error` to the UI.

import { ApiException } from '@/api/errors';
import {
  ALLOWED_PHOTO_MIME_TYPES,
  MAX_PHOTO_BYTES,
  PhotoUploadToR2Error,
  photosApi,
  type AllowedPhotoMimeType,
} from '@/api/reportsApi';
import type { ReportPhotoJson } from '@/api/types';
import type { FileValidationResult, UploadTask } from './photoUploadTypes';

/** Minimal context the service needs from the controller. */
export interface UploadContext {
  reportId: string;
  /** Bearer-token provider — same shape AuthContext exposes. */
  getToken: () => string | null;
}

export class PhotoUploadPipelineError extends Error {
  readonly phase: 'upload-url' | 'r2-original' | 'r2-thumbnail' | 'finalize';
  readonly statusCode: number;
  /** Backend-provided code if available (e.g. `SUBSCRIPTION_REQUIRED`). */
  readonly code: string | null;
  constructor(params: {
    phase: PhotoUploadPipelineError['phase'];
    statusCode: number;
    code: string | null;
    message: string;
  }) {
    super(params.message);
    this.name = 'PhotoUploadPipelineError';
    this.phase = params.phase;
    this.statusCode = params.statusCode;
    this.code = params.code;
  }
}

/**
 * Validate a single File against the Backend's `photoUploadUrlSchema`
 * (MIME allowlist + 15 MiB ceiling). Pure function so the UI can
 * present one error banner for the entire batch before kicking off
 * any upload-url request.
 *
 * The MIME allowlist mirrors `validators/photos.ts`; if Backend
 * ever adds a new MIME, update both. (We also accept `image/jpg`
 * even though Backend Zod lowercases the input — modern browsers
 * rarely emit `image/jpg` but some legacy capture pipelines do.)
 */
export function validateFile(file: File): FileValidationResult {
  const mime = file.type.toLowerCase();
  if (!isAllowedMime(mime)) {
    return {
      ok: false,
      message: `Unsupported file type: ${file.type || '(unknown)'}. Allowed: JPEG, PNG, HEIC, HEIF.`,
    };
  }
  if (file.size > MAX_PHOTO_BYTES) {
    return {
      ok: false,
      message: `File too large: ${(file.size / 1024 / 1024).toFixed(1)} MiB. Maximum is 15 MiB.`,
    };
  }
  if (file.size === 0) {
    return {
      ok: false,
      message: `File is empty: ${file.name}.`,
    };
  }
  return { ok: true };
}

function isAllowedMime(mime: string): mime is AllowedPhotoMimeType {
  return (ALLOWED_PHOTO_MIME_TYPES as readonly string[]).includes(mime);
}

/**
 * Run the four-step upload pipeline for one task.
 *
 * On success returns the resulting `ReportPhotoJson` so the
 * controller can add it directly to the committed-photos state
 * without a separate GET /photos round-trip.
 *
 * On failure throws a `PhotoUploadPipelineError` with the failing
 * phase. The controller catches it and marks the task `failed`,
 * preserving the same `clientPhotoId` for the next retry.
 */
export async function runUploadTask(
  task: UploadTask,
  ctx: UploadContext,
): Promise<ReportPhotoJson> {
  const { file, mimeType, sizeBytes, thumbnail, clientPhotoId } = task;
  const filename = file.name || `${clientPhotoId}.${extensionFor(mimeType)}`;

  // 1. request-upload-url ────────────────────────────────────────────────
  let presign: Awaited<ReturnType<typeof photosApi.requestUploadUrl>>;
  try {
    presign = await photosApi.requestUploadUrl(ctx.getToken, ctx.reportId, {
      client_photo_id: clientPhotoId,
      filename: filename.slice(0, 255),
      mime_type: mimeType,
      size_bytes: sizeBytes,
      ...(thumbnail ? { thumbnail_bytes: thumbnail.bytes } : {}),
    });
  } catch (err) {
    throw wrapApiException(err, 'upload-url');
  }

  // 2. PUT original to R2 (raw fetch, NO Bearer) ────────────────────────
  try {
    await photosApi.uploadToR2({
      uploadUrl: presign.upload_url,
      headers: presign.headers,
      body: file,
    });
  } catch (err) {
    throw wrapR2Error(err, 'r2-original');
  }

  // 3. PUT thumbnail to R2 (only when the Backend offered a slot) ────────
  if (presign.thumbnail_upload_url && thumbnail && presign.thumbnail_headers) {
    try {
      await photosApi.uploadToR2({
        uploadUrl: presign.thumbnail_upload_url,
        headers: presign.thumbnail_headers,
        body: thumbnail.blob,
      });
    } catch (err) {
      throw wrapR2Error(err, 'r2-thumbnail');
    }
  }

  // 4. finalize ──────────────────────────────────────────────────────────
  let serverPhoto: ReportPhotoJson;
  try {
    serverPhoto = await photosApi.finalizeUpload(ctx.getToken, ctx.reportId, {
      photo_id: presign.photo_id,
      size_bytes: sizeBytes,
      ...(thumbnail ? { width: thumbnail.width, height: thumbnail.height } : {}),
    });
  } catch (err) {
    throw wrapApiException(err, 'finalize');
  }
  return serverPhoto;
}

/**
 * Convenience: read the dimension of a freshly-decoded thumbnail
 * without storing the bitmap. Used by callers that want to surface
 * the final image dimensions on finalize (the Backend Zod schema
 * accepts `width` / `height` as optional but they're useful for
 * the gallery layout).
 */
export function extensionFor(mimeType: AllowedPhotoMimeType): string {
  switch (mimeType) {
    case 'image/jpeg':
    case 'image/jpg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/heic':
    case 'image/heif':
      return 'heic';
    default:
      return 'bin';
  }
}

function wrapApiException(
  err: unknown,
  phase: PhotoUploadPipelineError['phase'],
): PhotoUploadPipelineError {
  if (err instanceof ApiException) {
    return new PhotoUploadPipelineError({
      phase,
      statusCode: err.statusCode,
      code: err.code ?? null,
      message: err.message || `Photo upload failed at ${phase} (HTTP ${err.statusCode})`,
    });
  }
  if (err instanceof Error) {
    return new PhotoUploadPipelineError({
      phase,
      statusCode: 0,
      code: null,
      message: err.message,
    });
  }
  return new PhotoUploadPipelineError({
    phase,
    statusCode: 0,
    code: null,
    message: `Photo upload failed at ${phase}`,
  });
}

function wrapR2Error(
  err: unknown,
  phase: PhotoUploadPipelineError['phase'],
): PhotoUploadPipelineError {
  if (err instanceof PhotoUploadToR2Error) {
    return new PhotoUploadPipelineError({
      phase,
      statusCode: err.statusCode,
      code: null,
      message: err.message,
    });
  }
  if (err instanceof Error) {
    return new PhotoUploadPipelineError({
      phase,
      statusCode: 0,
      code: null,
      message: err.message,
    });
  }
  return new PhotoUploadPipelineError({
    phase,
    statusCode: 0,
    code: null,
    message: `Photo upload failed at ${phase}`,
  });
}
